import { useRef, useState, useMemo } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';
import { useClickOutside } from '../hooks/useClickOutside';
import { cn } from '../utils/cn';

export interface MultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface MultiSelectProps {
  label?: string;
  value: string[];
  onChange: (values: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  searchable?: boolean;
  maxSelected?: number;
  error?: string;
  disabled?: boolean;
  className?: string;
}

export function MultiSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select...',
  searchable = true,
  maxSelected,
  error,
  disabled,
  className,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (v: string) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else {
      if (maxSelected && value.length >= maxSelected) return;
      onChange([...value, v]);
    }
  };

  const remove = (v: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((x) => x !== v));
  };

  const selectedLabels = value
    .map((v) => options.find((o) => o.value === v))
    .filter(Boolean) as MultiSelectOption[];

  return (
    <div className={cn('flex w-full flex-col gap-1', className)} ref={ref}>
      {label && (
        <label className="text-xs font-medium text-content-secondary">
          {label}
        </label>
      )}
      <div
        onClick={() => !disabled && setOpen((o) => !o)}
        className={cn(
          'flex min-h-10 w-full cursor-pointer items-center gap-1 rounded-md border px-2 py-1.5 transition-colors',
          open ? 'border-primary' : 'border-border',
          error && 'border-danger',
          disabled && 'opacity-50 cursor-not-allowed',
          'bg-surface-card'
        )}
      >
        <div className="flex flex-1 flex-wrap items-center gap-1">
          {selectedLabels.length === 0 ? (
            <span className="text-sm text-content-muted">{placeholder}</span>
          ) : (
            selectedLabels.map((opt) => (
              <span
                key={opt.value}
                className="inline-flex items-center gap-1 rounded-full bg-primary-light px-2 py-0.5 text-xs font-medium text-primary"
              >
                {opt.label}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => remove(opt.value, e)}
                    className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full hover:bg-primary/20"
                    aria-label={`Remove ${opt.label}`}
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                )}
              </span>
            ))
          )}
        </div>
        <ChevronDown className={cn('h-4 w-4 text-content-muted transition-transform', open && 'rotate-180')} />
      </div>

      {open && (
        <div className="relative">
          <div className="absolute left-0 right-0 top-1 z-50 rounded-md border border-border bg-surface-card shadow-xl">
            {searchable && (
              <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                <Search className="h-3.5 w-3.5 text-content-muted" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full bg-transparent text-sm text-content-primary outline-none placeholder:text-content-muted"
                />
              </div>
            )}
            <div className="max-h-60 overflow-y-auto py-1">
              {filtered.length === 0 && (
                <div className="px-3 py-2 text-xs text-content-muted">No matches</div>
              )}
              {filtered.map((opt) => {
                const selected = value.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={opt.disabled}
                    onClick={() => toggle(opt.value)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors disabled:opacity-40',
                      selected
                        ? 'bg-primary-light text-primary font-medium'
                        : 'text-content-secondary hover:bg-surface-card-hover'
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border-strong'
                      )}
                    >
                      {selected && (
                        <svg viewBox="0 0 20 20" className="h-3 w-3" fill="currentColor">
                          <path d="M7.629 13.657l-3.157-3.157 1.414-1.414 1.743 1.743 4.243-4.243 1.414 1.414z" />
                        </svg>
                      )}
                    </span>
                    <span className="flex-1 truncate">{opt.label}</span>
                  </button>
                );
              })}
            </div>
            {value.length > 0 && (
              <div className="flex justify-between border-t border-border px-3 py-2">
                <span className="text-xs text-content-muted">
                  {value.length} selected{maxSelected ? ` / ${maxSelected}` : ''}
                </span>
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {error && <span className="text-xs text-danger">{error}</span>}
    </div>
  );
}

export default MultiSelect;
